import React from 'react';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { Plus, Minus, RotateCcw } from 'lucide-react';

const KOREA_GEO_URL = 'https://raw.githubusercontent.com/southkorea/southkorea-maps/master/kostat/2013/json/skorea_provinces_topo.json';

interface KoreaMapProps {
  regionData: { name: string; koreaTeam: number; csTeam: number; value: number }[];
  selectedRegion: string | null;
  onSelectRegion: (region: string | null) => void;
  mapTeamFilter: '전체' | '국내팀' | 'CS팀'; 
}

const KoreaMap: React.FC<KoreaMapProps> = ({ regionData, selectedRegion, onSelectRegion, mapTeamFilter }) => {
  
  const regionMarkers = [
    { id: "서울", coordinates: [126.9780, 37.5665] },
    { id: "경기", coordinates: [127.4, 37.45] },
    { id: "인천", coordinates: [126.45, 37.45] },
    { id: "강원", coordinates: [128.3, 37.75] },
    { id: "충북", coordinates: [127.8, 36.85] },
    { id: "충남", coordinates: [126.7, 36.55] },
    { id: "대전", coordinates: [127.4, 36.35] },
    { id: "세종", coordinates: [127.25, 36.5] },
    { id: "전북", coordinates: [127.1, 35.75] },
    { id: "전남", coordinates: [126.85, 34.85] },
    { id: "광주", coordinates: [126.85, 35.15] },
    { id: "경북", coordinates: [128.85, 36.45] },
    { id: "경남", coordinates: [128.45, 35.3] },
    { id: "대구", coordinates: [128.6, 35.85] },
    { id: "부산", coordinates: [129.1, 35.18] },
    { id: "울산", coordinates: [129.3, 35.54] },
    { id: "제주", coordinates: [126.55, 33.38] },
  ];

  // 🚨 [추가] 지도(땅)를 클릭했을 때 지역 이름을 매칭해주는 함수
  const handleRegionClick = (geo: any) => {
    // 지도 데이터(JSON) 안에는 보통 '서울특별시', '강원도' 형태로 이름이 들어있습니다.
    const fullName = geo.properties.CTP_KOR_NM || geo.properties.name || geo.properties.name_1 || "";
    let shortName = "";

    // 2글자 짧은 이름으로 변환
    if (fullName.includes("서울")) shortName = "서울";
    else if (fullName.includes("경기")) shortName = "경기";
    else if (fullName.includes("인천")) shortName = "인천";
    else if (fullName.includes("강원")) shortName = "강원";
    else if (fullName.includes("충청북") || fullName.includes("충북")) shortName = "충북";
    else if (fullName.includes("충청남") || fullName.includes("충남")) shortName = "충남";
    else if (fullName.includes("대전")) shortName = "대전";
    else if (fullName.includes("세종")) shortName = "세종";
    else if (fullName.includes("전라북") || fullName.includes("전북")) shortName = "전북";
    else if (fullName.includes("전라남") || fullName.includes("전남")) shortName = "전남";
    else if (fullName.includes("광주")) shortName = "광주";
    else if (fullName.includes("경상북") || fullName.includes("경북")) shortName = "경북";
    else if (fullName.includes("경상남") || fullName.includes("경남")) shortName = "경남";
    else if (fullName.includes("대구")) shortName = "대구";
    else if (fullName.includes("부산")) shortName = "부산";
    else if (fullName.includes("울산")) shortName = "울산";
    else if (fullName.includes("제주")) shortName = "제주";

    if (shortName) {
      const isSelected = selectedRegion === shortName;
      onSelectRegion(isSelected ? null : shortName);
    }
  };

  return (
    <div className="w-full h-full flex flex-col items-center justify-center relative bg-white rounded-3xl overflow-hidden p-0 group">
      <TransformWrapper initialScale={1} minScale={0.8} maxScale={5} centerOnInit={true} wheel={{ step: 0.1 }}>
        {({ zoomIn, zoomOut, resetTransform }) => (
          <>
            <div className="absolute bottom-4 right-4 z-10 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <button onClick={() => zoomIn()} className="p-2 bg-white/80 backdrop-blur-sm border border-slate-200 rounded-lg shadow-md hover:bg-white text-slate-600 transition-all" title="확대"><Plus size={16} strokeWidth={3} /></button>
              <button onClick={() => zoomOut()} className="p-2 bg-white/80 backdrop-blur-sm border border-slate-200 rounded-lg shadow-md hover:bg-white text-slate-600 transition-all" title="축소"><Minus size={16} strokeWidth={3} /></button>
              <button onClick={() => resetTransform()} className="p-2 bg-white/80 backdrop-blur-sm border border-slate-200 rounded-lg shadow-md hover:bg-white text-slate-600 transition-all" title="위치 초기화"><RotateCcw size={16} strokeWidth={3} /></button>
            </div>

            <TransformComponent wrapperStyle={{ width: "100%", height: "100%" }} contentStyle={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <div className="w-full h-full cursor-grab active:cursor-grabbing flex items-center justify-center">
                <ComposableMap
                  projection="geoMercator"
                  projectionConfig={{ center: [127.8, 36.1], scale: 7500 }}
                  style={{ width: "100%", height: "100%" }}
                  viewBox="0 0 700 800"
                >
                  <Geographies geography={KOREA_GEO_URL}>
                    {({ geographies }: { geographies: any[] }) =>
                      geographies.map((geo: any) => {
                        // 🚨 [추가] 지도 자체에 선택된 상태 표시 로직 추가 (선택된 지역은 파란색으로 살짝 칠해짐)
                        const fullName = geo.properties.CTP_KOR_NM || geo.properties.name || "";
                        const isThisRegionSelected = selectedRegion && fullName.includes(selectedRegion.replace('충남', '충청남').replace('충북', '충청북').replace('전남', '전라남').replace('전북', '전라북').replace('경남', '경상남').replace('경북', '경상북'));

                        return (
                          <Geography
                            key={geo.rsmKey}
                            geography={geo}
                            // 🚨 [추가] 온클릭 이벤트 달기
                            onClick={() => handleRegionClick(geo)} 
                            fill={isThisRegionSelected ? "#e0e7ff" : "#f8fafc"} 
                            stroke={isThisRegionSelected ? "#818cf8" : "#cbd5e1"} 
                            strokeWidth={isThisRegionSelected ? 1.5 : 0.6}
                            style={{
                              default: { outline: "none", cursor: "pointer", transition: "all 300ms" },
                              hover: { fill: "#e2e8f0", outline: "none", cursor: "pointer", transition: "all 300ms" },
                              pressed: { outline: "none", cursor: "pointer" },
                            }}
                          />
                        );
                      })
                    }
                  </Geographies>

                  {regionMarkers.map((marker) => {
                    const regionInfo = regionData.find(r => r.name.includes(marker.id) || marker.id.includes(r.name));
                    if (!regionInfo) return null;

                    const koreaCount = regionInfo.koreaTeam || 0;
                    const csCount = regionInfo.csTeam || 0;
                    const isSelected = selectedRegion === marker.id;

                    const showKorea = (mapTeamFilter === '전체' || mapTeamFilter === '국내팀') && koreaCount > 0;
                    const showCS = (mapTeamFilter === '전체' || mapTeamFilter === 'CS팀') && csCount > 0;

                    if (!showKorea && !showCS) return null;

                    return (
                      <Marker key={marker.id} coordinates={marker.coordinates as [number, number]}>
                        <g onClick={() => onSelectRegion(isSelected ? null : marker.id)} className="cursor-pointer transition-all duration-300 active:scale-90">
                          
                          <text textAnchor="middle" y={isSelected ? 32 : 24} fill={isSelected ? "#1e293b" : "#475569"} fontSize={10} fontWeight="900" style={{ pointerEvents: "none", paintOrder: "stroke", stroke: "#ffffff", strokeWidth: 2.5 }}>
                            {marker.id}
                          </text>

                          {/* 🔵 국내팀 마커 */}
                          {showKorea && (
                            <g transform={showCS ? "translate(-12, 0)" : "translate(0, 0)"}>
                              <circle r={isSelected ? 16 : 12} fill="#60a5fa" opacity="0.2" />
                              <circle r={isSelected ? 13 : 10} fill="white" className="shadow-lg" />
                              <circle r={isSelected ? 11 : 8} fill="#3b82f6" />
                              <text textAnchor="middle" dy="0.35em" fill="white" fontSize={isSelected ? 10 : 8} fontWeight="900" style={{ pointerEvents: "none" }}>
                                {koreaCount}
                              </text>
                            </g>
                          )}

                          {/* 🔴 CS팀 마커 */}
                          {showCS && (
                            <g transform={showKorea ? "translate(12, 0)" : "translate(0, 0)"}>
                              <circle r={isSelected ? 16 : 12} fill="#f87171" opacity="0.2" />
                              <circle r={isSelected ? 13 : 10} fill="white" className="shadow-lg" />
                              <circle r={isSelected ? 11 : 8} fill="#ef4444" />
                              <text textAnchor="middle" dy="0.35em" fill="white" fontSize={isSelected ? 10 : 8} fontWeight="900" style={{ pointerEvents: "none" }}>
                                {csCount}
                              </text>
                            </g>
                          )}

                        </g>
                      </Marker>
                    );
                  })}
                </ComposableMap>
              </div>
            </TransformComponent>
          </>
        )}
      </TransformWrapper>
    </div>
  );
};

export default KoreaMap;